import { Badge } from "@deere/fuel-react";
import {
  AccessTimeOutlined,
  AltRoute,
  AutorenewOutlined,
  Cancel,
  CheckCircle,
  DirectionsRun,
  Info,
  LocalShipping,
  Person,
  Send,
  ShoppingCart,
  Store,
  TimerOffOutlined,
  Warning,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { LineStatus } from "../../types";

interface LineStatusIconInfo {
  icon: React.ReactElement;
  variant?: "error" | "warning" | "info" | "success" | undefined;
  badgeSx?: {};
}
const LineStatusBadge = ({ status }: { status: LineStatus | string }) => {
  const theme = useTheme();
  const { t } = useTranslation("orders");

  const iconMap: Record<string, LineStatusIconInfo> = {
    [LineStatus.CONFIRMED]: { icon: <CheckCircle />, variant: "success" },
    [LineStatus.BACK_ORDERED]: {
      icon: <AccessTimeOutlined />,
      variant: "warning",
    },
    [LineStatus.SENT_TO_SUPPLIER]: { icon: <LocalShipping />, variant: "info" },
    [LineStatus.DEERE_INVESTIGATION]: { icon: <Info />, variant: "info" },
    [LineStatus.IN_PROCESS]: { icon: <DirectionsRun />, variant: "info" },
    [LineStatus.SENT_TO_FACTORY]: { icon: <Store />, variant: "info" },
    [LineStatus.DEALER_INVESTIGATION]: {
      icon: <Warning />,
      variant: "warning",
    },
    [LineStatus.USER_CANCELLED]: { icon: <Person />, variant: "error" },
    [LineStatus.AUTO_CANCELLED]: { icon: <Cancel />, variant: "error" },
    [LineStatus.SHIPPED]: {
      icon: (
        <CheckCircle
          sx={{ color: `${theme.tokens.FuelColorWhite} !important` }}
        />
      ),
      badgeSx: {
        bgcolor: theme.tokens.FuelColorAlertSuccessIcon,
        color: theme.tokens.FuelColorWhite,
      },
    },
    [LineStatus.ON_HOLD]: { icon: <TimerOffOutlined />, variant: "warning" },
    [LineStatus.PARTIALLY_SHIPPED]: {
      icon: <CheckCircle />,
      variant: "success",
    },
    [LineStatus.CANCELLED]: { icon: <Cancel />, variant: "error" },
    [LineStatus.ORDERED]: { icon: <ShoppingCart /> },
    [LineStatus.ROUTED]: { icon: <AutorenewOutlined /> },
    CANCELLATION_INITIATED: { icon: <Info />, variant: "info" },
    [LineStatus.EXPORT_RESHIP]: { icon: <Store />, variant: "info" },
    [LineStatus.ROUTED_TO_DEALER]: { icon: <AltRoute />, variant: "info" },
    [LineStatus.SENT_TO_DEALER]: { icon: <Send />, variant: "info" },
    [LineStatus.SHIPPED_BY_DEALER]: {
      icon: <AccessTimeOutlined />,
      variant: "info",
    },
  };

  const { icon, variant, badgeSx } = iconMap[status];
  return (
    <Badge
      startIcon={icon}
      variant={variant}
      sx={badgeSx}
      data-testid="line-status-badge"
    >
      {t(`orders:lineStatus.${status}`)}
    </Badge>
  );
};

export default LineStatusBadge;
